import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import { Alert } from "@material-ui/lab";
import { getServiceNameWithNickname } from "../../../../utils/getServiceNameWithNickname";
import ExchangeDetailsFlowData from "../../../elements/exchange/ExchangeDetailsFlowData";
import ExchangeFeeHistoryContent from "../../../elements/exchange/ExchangeFeeHistoryContent";
import Notification from "../../../elements/notification/Notification";
import GoToBackButton from "../../../elements/goToBack/GoToBack";
import RetryCallbackButton from "../../../elements/retryCallbackButton/RetryCallbackButton";
import PageSpinner from "../../../elements/spinner/PageSpinner";

import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import { parseAxiosError } from "../../../../utils/response";
import { TimestampToDateMoment } from "../../../../utils/timestampToDate";
import { HTTP_STATUSES, STANDARD_ERRORS_WAY } from "../../../../utils/consts";

import { AppContext } from "../../../../App";

import { StyledBreakWord } from "../../../styles/styledBreakWord";
import { StyledCopy } from "../../../styles/styledCopy";
import RenderStatus from "../../../styles/styledStatus";
import { StyledExchangeHistoryDetailsWrapper } from "../../styledExchange";
import { StyledContentWrapper } from "../../../styles/styledMain";
import { StyledCol, StyledTable, StyledTableBody, StyledTableHead, StyledTableRow } from "../../../styles/styledTable";
import { StyledBlockLabel, StyledFormBlock, StyledFormGrid } from "../../../styles/styledTabsForm";

const ExchangeHistoryDetailsContainer = ({ match }) => {
  const { t } = useTranslation("exchangerPage");
  const { user } = useContext(AppContext);

  const history = useHistory();

  const [exchangeDetails, setExchangeDetails] = useState(null);
  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  const fetchExchangeDetails = () => {
    axios.get(`/api/exchanges/${match.params.id}`, userAuthenticationConfig()).then(response => {
      if (response.status === HTTP_STATUSES.HTTP_OK) {
        setExchangeDetails(response.data);
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    });
  };

  useEffect(() => {
    fetchExchangeDetails();
  }, []);

  if (!exchangeDetails) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (exchangeDetails.length === 0) {
    return <Alert severity="warning">{t("history.details.content.emptyData")}</Alert>;
  }

  const {
    id,
    createdAt,
    inAmount,
    outAmount,
    pair,
    exchangeFeeHistories,
    status,
    flowData,
    exchangePhysicalFeeHistories,
    profit
  } = exchangeDetails;

  const feeHistories = exchangeFeeHistories.length !== 0 ? exchangeFeeHistories : exchangePhysicalFeeHistories;

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledContentWrapper>
        <Helmet>
          <title>{t("history.details.meta.title")}</title>
        </Helmet>
        <StyledExchangeHistoryDetailsWrapper>
          <div className="exchange-history-details-head">
            <GoToBackButton
              handler={() => {history.goBack();}}
            />
            <RetryCallbackButton
              id={match.params.id}
              type="exchanges"
              setNotification={setNotification}
              notification={notification}
            />
          </div>
          <StyledFormGrid className="exchange-history-details">
            <StyledFormBlock className="exchange-history-details__block">
              <StyledBlockLabel>
                {t("history.details.content.exchange")}
              </StyledBlockLabel>
              <StyledTable size="16">
                <StyledTableHead col="2" className="exchange-history-details__head">
                  <StyledCol>
                    {t("history.details.content.key")}:
                  </StyledCol>
                  <StyledCol>
                    {t("history.details.content.value")}:
                  </StyledCol>
                </StyledTableHead>
                <StyledTableBody>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      Id
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      <CopyToClipboard
                        text={id}
                        onCopy={() => {
                          setNotification({
                            ...notification,
                            visible: true,
                            type: "success",
                            message: t("copied")
                          });
                        }}
                      >
                        <StyledBreakWord as="p">
                          {id} <StyledCopy title={t("copy")} className="icon-light-copy" />
                        </StyledBreakWord>
                      </CopyToClipboard>
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.client")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      <CopyToClipboard
                        text={pair.user.email}
                        onCopy={() => {
                          setNotification({
                            ...notification,
                            visible: true,
                            type: "success",
                            message: t("copied")
                          });
                        }}
                      >
                        <StyledBreakWord as="p">
                          {pair.user.email} <StyledCopy title={t("copy")} className="icon-light-copy" />
                        </StyledBreakWord>
                      </CopyToClipboard>
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.status")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      <RenderStatus status={status?.value} />
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.inService")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {getServiceNameWithNickname(pair.exchangePair.inService.name, pair.exchangePair.inService.nickname)}
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.inAmount")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {inAmount} {pair.exchangePair.inCurrency.asset}
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.outAmount")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {outAmount} {pair.exchangePair.outCurrency.asset}
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.profit")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {profit} {pair.exchangePair.inCurrency.asset}
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.outService")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {getServiceNameWithNickname(pair.exchangePair.outService.name, pair.exchangePair.outService.nickname)}
                    </StyledCol>
                  </StyledTableRow>
                  <StyledTableRow col="2" className="exchange-history-details__row">
                    <StyledCol data-title={t("key")}>
                      {t("history.details.content.date")}
                    </StyledCol>
                    <StyledCol data-title={t("value")}>
                      {TimestampToDateMoment(createdAt)}
                    </StyledCol>
                  </StyledTableRow>
                </StyledTableBody>
              </StyledTable>
            </StyledFormBlock>
            <ExchangeFeeHistoryContent exchangeFeeHistories={feeHistories} t={t} />
            <ExchangeDetailsFlowData flowData={flowData} t={t} />
          </StyledFormGrid>
        </StyledExchangeHistoryDetailsWrapper>
      </StyledContentWrapper>
    </>
  );
};

export default ExchangeHistoryDetailsContainer;